import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import * as Sentry from '@sentry/capacitor';
import {
  IndexedDBBlobStore,
  type MultipartProgress,
  type MultipartErrorContext,
  type MultipartUpload,
} from '@cc-consulting-nv/ccsdk';
import { getSdk } from '@/composables/useSdk';

export type UploadJobStatus = 'queued' | 'uploading' | 'completed' | 'error' | 'paused';

export type UploadMediaType = 'audio' | 'image' | 'video' | 'file';

export interface UploadJob {
  id: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  mediaType: UploadMediaType;
  status: UploadJobStatus;
  pct: number;
  uploadedBytes: number;
  totalBytes: number;
  uploadId: string | null;
  key: string | null;
  completedParts: number[];
  partSize: number;
  resultUrl: string | null;
  errorMessage: string | null;
  createdAt: number;
}

export interface PersistedUploadJob {
  id: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  mediaType: UploadMediaType;
  uploadId: string;
  key: string;
  partSize: number;
  completedParts: number[];
  createdAt: number;
}

interface ActiveJob {
  job: UploadJob;
  controller: MultipartUpload;
  resolve: (url: string) => void;
  reject: (err: Error) => void;
}

interface PendingMultipart {
  jobId: string;
  file: File;
  resolve: (url: string) => void;
  reject: (err: Error) => void;
}

const STORAGE_KEY = 'gunclub.uploadQueue.v1';
const DIRECT_UPLOAD_THRESHOLD = 10 * 1024 * 1024;
const MAX_PARALLEL_MULTIPART = 2;
const PER_UPLOAD_PART_CONCURRENCY = 3;

const blobStore = new IndexedDBBlobStore({ dbName: 'gunclub_upload_blobs' });

function loadPersisted(): PersistedUploadJob[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function savePersisted(jobs: PersistedUploadJob[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(jobs));
  } catch {
    /* ignore quota errors */
  }
}

function makeJobId(): string {
  return `up_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function reportUploadError(err: unknown, ctx: Record<string, unknown>): void {
  try {
    Sentry.withScope((scope) => {
      scope.setTag('error_source', 'upload');
      scope.setContext('upload', ctx);
      Sentry.captureException(err);
    });
  } catch {
    /* Sentry may not be initialized in dev */
  }
}

export const useUploadQueueStore = defineStore('uploadQueue', () => {
  const jobs = ref<UploadJob[]>([]);
  const persistedOrphans = ref<PersistedUploadJob[]>(loadPersisted());
  const activeJobs = new Map<string, ActiveJob>();
  const pendingMultiparts: PendingMultipart[] = [];

  const activeJob = computed<UploadJob | null>(() => {
    return jobs.value.find((j) => j.status === 'uploading' || j.status === 'queued') ?? null;
  });

  const hasActiveUpload = computed(() => activeJob.value !== null);

  const overallPct = computed(() => {
    const active = jobs.value.filter((j) => j.status === 'uploading' || j.status === 'queued');
    if (active.length === 0) return 0;
    const totalBytes = active.reduce((sum, j) => sum + j.totalBytes, 0);
    const uploadedBytes = active.reduce((sum, j) => sum + j.uploadedBytes, 0);
    return totalBytes > 0 ? (uploadedBytes / totalBytes) * 100 : 0;
  });

  // Test hook for Playwright. Dev-only — stripped in prod builds.
  if (import.meta.env.DEV && typeof window !== 'undefined') {
    const win = window as unknown as {
      __uploadDebug?: {
        pct: number;
        status: string;
        resultUrl: string | null;
        uploadedBytes: number;
        totalBytes: number;
        jobId: string | null;
      };
    };
    const update = (): void => {
      const target =
        jobs.value.find((j) => j.status === 'uploading' || j.status === 'queued') ??
        jobs.value[jobs.value.length - 1] ??
        null;
      win.__uploadDebug = target
        ? {
            pct: target.pct,
            status: target.status,
            resultUrl: target.resultUrl,
            uploadedBytes: target.uploadedBytes,
            totalBytes: target.totalBytes,
            jobId: target.id,
          }
        : { pct: 0, status: 'idle', resultUrl: null, uploadedBytes: 0, totalBytes: 0, jobId: null };
    };
    setInterval(update, 250);
  }

  function persistAll(): void {
    const persistable: PersistedUploadJob[] = jobs.value
      .filter((j) => j.uploadId && j.key && j.status !== 'completed')
      .map((j) => ({
        id: j.id,
        fileName: j.fileName,
        fileSize: j.fileSize,
        fileType: j.fileType,
        mediaType: j.mediaType,
        uploadId: j.uploadId!,
        key: j.key!,
        partSize: j.partSize,
        completedParts: j.completedParts,
        createdAt: j.createdAt,
      }));
    savePersisted(persistable);
  }

  function updateJob(id: string, patch: Partial<UploadJob>): void {
    const idx = jobs.value.findIndex((j) => j.id === id);
    if (idx === -1) return;
    jobs.value[idx] = { ...jobs.value[idx], ...patch };
    persistAll();
  }

  async function enqueueDirectUpload(job: UploadJob, file: File, userUlid: string): Promise<string> {
    updateJob(job.id, { status: 'uploading' });
    try {
      const sdk = getSdk();
      const result = await sdk.uploadMediaFile(file, {
        userUlid,
        mediaType: job.mediaType,
        onProgress: (pct) => {
          updateJob(job.id, {
            pct,
            uploadedBytes: Math.round((pct / 100) * job.totalBytes),
          });
        },
      });
      updateJob(job.id, {
        status: 'completed',
        pct: 100,
        uploadedBytes: job.totalBytes,
        resultUrl: result.url,
      });
      return result.url;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Upload failed';
      updateJob(job.id, { status: 'error', errorMessage });
      reportUploadError(err, {
        path: 'direct',
        jobId: job.id,
        fileName: job.fileName,
        fileSize: job.fileSize,
        fileType: job.fileType,
        mediaType: job.mediaType,
      });
      throw err;
    }
  }

  function buildMultipartUpload(
    jobId: string,
    file: File,
    options: {
      uploadId?: string;
      key?: string;
      partSize?: number;
      completedParts?: number[];
      onComplete: (location: string) => void;
      onError: (err: Error) => void;
    },
  ): MultipartUpload {
    const sdk = getSdk();
    return sdk.createMultipartUpload({
      file,
      jobId,
      uploadId: options.uploadId,
      key: options.key,
      partSize: options.partSize,
      completedParts: options.completedParts,
      blobStore,
      maxConcurrentUploads: PER_UPLOAD_PART_CONCURRENCY,
      onByteProgress: (progress: MultipartProgress) => {
        // Mirror SDK uploadId/key/partSize into the job once initialize() runs.
        const upload = activeJobs.get(jobId)?.controller;
        const patch: Partial<UploadJob> = {
          pct: progress.percentage,
          uploadedBytes: progress.uploadedBytes,
          totalBytes: progress.totalBytes,
          status: 'uploading',
        };
        if (upload) {
          const id = upload.getUploadId();
          const key = upload.getKey();
          if (id) patch.uploadId = id;
          if (key) patch.key = key;
        }
        updateJob(jobId, patch);
      },
      onPartComplete: (partNumber) => {
        const current = jobs.value.find((j) => j.id === jobId);
        if (!current) return;
        const completedParts = current.completedParts.includes(partNumber)
          ? current.completedParts
          : [...current.completedParts, partNumber];
        updateJob(jobId, { completedParts });
      },
      onComplete: options.onComplete,
      onError: options.onError,
      errorReporter: (err: Error, ctx: MultipartErrorContext) => {
        reportUploadError(err, { path: 'multipart', jobId, ...ctx });
      },
    });
  }

  function startMultipart(pending: PendingMultipart): void {
    const { jobId, file, resolve, reject } = pending;

    const upload = buildMultipartUpload(jobId, file, {
      onComplete: (location) => {
        const current = jobs.value.find((j) => j.id === jobId);
        updateJob(jobId, {
          status: 'completed',
          pct: 100,
          uploadedBytes: current?.totalBytes ?? file.size,
          resultUrl: location,
        });
        activeJobs.delete(jobId);
        resolve(location);
        pumpScheduler();
      },
      onError: (error) => {
        updateJob(jobId, { status: 'error', errorMessage: error.message });
        activeJobs.delete(jobId);
        reject(error);
        pumpScheduler();
      },
    });

    const jobRef = jobs.value.find((j) => j.id === jobId);
    if (!jobRef) {
      reject(new Error('Job removed before start'));
      pumpScheduler();
      return;
    }

    activeJobs.set(jobId, { job: jobRef, controller: upload, resolve, reject });

    upload.start().catch((err) => {
      if (jobs.value.find((j) => j.id === jobId)?.status !== 'error') {
        updateJob(jobId, {
          status: 'error',
          errorMessage: err instanceof Error ? err.message : 'Upload failed',
        });
      }
      activeJobs.delete(jobId);
      reject(err instanceof Error ? err : new Error(String(err)));
      pumpScheduler();
    });
  }

  function pumpScheduler(): void {
    while (activeJobs.size < MAX_PARALLEL_MULTIPART && pendingMultiparts.length > 0) {
      const next = pendingMultiparts.shift();
      if (!next) return;
      const job = jobs.value.find((j) => j.id === next.jobId);
      if (!job || job.status === 'error') {
        next.reject(new Error('Job no longer queued'));
        continue;
      }
      startMultipart(next);
    }
  }

  function enqueueMultipartUpload(job: UploadJob, file: File): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      pendingMultiparts.push({ jobId: job.id, file, resolve, reject });
      pumpScheduler();
    });
  }

  async function enqueue(
    file: File,
    options: { mediaType: UploadMediaType; userUlid: string },
  ): Promise<string> {
    const job: UploadJob = {
      id: makeJobId(),
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      mediaType: options.mediaType,
      status: 'queued',
      pct: 0,
      uploadedBytes: 0,
      totalBytes: file.size,
      uploadId: null,
      key: null,
      completedParts: [],
      partSize: 0,
      resultUrl: null,
      errorMessage: null,
      createdAt: Date.now(),
    };
    jobs.value.push(job);

    if (file.size < DIRECT_UPLOAD_THRESHOLD) {
      return enqueueDirectUpload(job, file, options.userUlid);
    }
    return enqueueMultipartUpload(job, file);
  }

  async function cancel(id: string): Promise<void> {
    const active = activeJobs.get(id);
    if (active) {
      await active.controller.abort();
      activeJobs.delete(id);
    }
    const pendingIdx = pendingMultiparts.findIndex((p) => p.jobId === id);
    if (pendingIdx >= 0) {
      const [pending] = pendingMultiparts.splice(pendingIdx, 1);
      pending.reject(new Error('Cancelled'));
    }
    updateJob(id, { status: 'error', errorMessage: 'Cancelled' });
    void blobStore.delete(id);
    pumpScheduler();
  }

  function remove(id: string): void {
    jobs.value = jobs.value.filter((j) => j.id !== id);
    persistAll();
  }

  function clearCompleted(): void {
    jobs.value = jobs.value.filter((j) => j.status !== 'completed');
    persistAll();
  }

  function discardOrphan(id: string): void {
    persistedOrphans.value = persistedOrphans.value.filter((o) => o.id !== id);
    savePersisted(persistedOrphans.value);
    void blobStore.delete(id);
  }

  function clearOrphans(): void {
    const ids = persistedOrphans.value.map((o) => o.id);
    persistedOrphans.value = [];
    savePersisted([]);
    ids.forEach((id) => void blobStore.delete(id));
  }

  /**
   * Try to silently resume an orphan using a previously stashed blob.
   * Returns the upload promise if blob is available; null otherwise so
   * the UI can fall back to the manual file picker.
   */
  async function tryAutoResumeOrphan(id: string): Promise<Promise<string> | null> {
    const orphan = persistedOrphans.value.find((o) => o.id === id);
    if (!orphan) return null;
    const file = await blobStore.get(id);
    if (!file) return null;
    return resumeOrphan(orphan, file);
  }

  async function resumeOrphan(orphan: PersistedUploadJob, file: File): Promise<string> {
    if (file.size !== orphan.fileSize || file.name !== orphan.fileName) {
      throw new Error('Selected file does not match the upload to resume');
    }

    const job: UploadJob = {
      id: orphan.id,
      fileName: orphan.fileName,
      fileSize: orphan.fileSize,
      fileType: orphan.fileType,
      mediaType: orphan.mediaType,
      status: 'uploading',
      pct: (orphan.completedParts.length * orphan.partSize) / orphan.fileSize * 100,
      uploadedBytes: Math.min(orphan.fileSize, orphan.completedParts.length * orphan.partSize),
      totalBytes: orphan.fileSize,
      uploadId: orphan.uploadId,
      key: orphan.key,
      completedParts: [...orphan.completedParts],
      partSize: orphan.partSize,
      resultUrl: null,
      errorMessage: null,
      createdAt: orphan.createdAt,
    };
    jobs.value.push(job);
    persistedOrphans.value = persistedOrphans.value.filter((o) => o.id !== orphan.id);
    savePersisted(persistedOrphans.value);

    return new Promise<string>((resolve, reject) => {
      const upload = buildMultipartUpload(job.id, file, {
        uploadId: orphan.uploadId,
        key: orphan.key,
        partSize: orphan.partSize,
        completedParts: orphan.completedParts,
        onComplete: (location) => {
          updateJob(job.id, {
            status: 'completed',
            pct: 100,
            uploadedBytes: job.totalBytes,
            resultUrl: location,
          });
          activeJobs.delete(job.id);
          void blobStore.delete(job.id);
          resolve(location);
        },
        onError: (error) => {
          updateJob(job.id, { status: 'error', errorMessage: error.message });
          activeJobs.delete(job.id);
          reject(error);
        },
      });

      activeJobs.set(job.id, { job, controller: upload, resolve, reject });

      upload.resume(orphan.uploadId, orphan.key).catch((err) => {
        updateJob(job.id, {
          status: 'error',
          errorMessage: err instanceof Error ? err.message : 'Resume failed',
        });
        activeJobs.delete(job.id);
        reject(err instanceof Error ? err : new Error(String(err)));
      });
    });
  }

  return {
    jobs,
    persistedOrphans,
    activeJob,
    hasActiveUpload,
    overallPct,
    enqueue,
    cancel,
    remove,
    clearCompleted,
    discardOrphan,
    clearOrphans,
    resumeOrphan,
    tryAutoResumeOrphan,
  };
});
